import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, FormLabel, FormControlLabel, Checkbox, RadioGroup, Switch } from '@mui/material';
import { GetNewsletterTypeList } from '../services/newsletter/NewsLetterService';
import i18n from '../i18n';
import FamiliplusPointBox from './common/Forms/FamiliplusPointBox';
import { NewsletterType } from './common/props-types';

export const NewsLetterCheckbox = (props: {
  formik: any;
  setError: Dispatch<SetStateAction<string>>;
  showPtsBox: boolean;
  showOnlyNewsletterContent: boolean;
  disableContentTypes: boolean;
  setNewsletterTypesSlug?: (value: string[] | ((prevVar: string[]) => string[])) => void;
}): JSX.Element => {
  const { t } = useTranslation('component');
  const [newsletterTypes, setNewsletterTypes] = useState<NewsletterType[]>([]);
  const SetFlsNewsletterTypes = async () => {
    try {
      const getNewsletterTypeListResponse = await GetNewsletterTypeList(i18n.resolvedLanguage ?? '');
      const newsletterTypeListResponse = await getNewsletterTypeListResponse.json();
      setNewsletterTypes(newsletterTypeListResponse.NewsLetterTypes);
    } catch (e) {
      props.setError('Errors.GenericError');
    }
  };

  useEffect(() => {
    SetFlsNewsletterTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    if (
      props.setNewsletterTypesSlug &&
      newsletterTypes.length > 0 &&
      props.formik.initialValues.newsletterTypes.length > 0
    ) {
      props.setNewsletterTypesSlug(
        newsletterTypes
          .filter((newsletterType) => props.formik.initialValues.newsletterTypes.includes(newsletterType.Id.toString()))
          .map((newsletterType) => newsletterType.Slug)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsletterTypes, props.formik.initialValues.newsletterTypes]);

  return (
    <>
      {!props.showOnlyNewsletterContent && (
        <>
          <FormLabel id="NewsLetterConsentLabel">
            {t('CommunicationPreferences.newsletterConsentTitle') + ' ' + t('Form.Optional')}
            {props.showPtsBox && <FamiliplusPointBox points="100"></FamiliplusPointBox>}
          </FormLabel>
          <RadioGroup aria-labelledby="NewsletterConsentLabel" onChange={props.formik.handleChange} sx={{ mb: '24px' }}>
            <FormControlLabel
              id="NewsletterConsent"
              control={
                <Checkbox
                  onChange={props.formik.handleChange}
                  name="newsletterConsent"
                  checked={props.formik.values.newsletterConsent}
                />
              }
              label={<Typography variant="body2">{t('CommunicationPreferences.newsletterConsentLabel')}</Typography>}
            />
          </RadioGroup>
        </>
      )}
      <NewsletterTypes
        disableContentTypes={props.disableContentTypes}
        newsletterTypes={newsletterTypes}
        formik={props.formik}
      />
    </>
  );
};

export const NewsletterTypes = (props: {
  newsletterTypes: NewsletterType[];
  formik: any;
  disableContentTypes: boolean;
  setNewsletterTypesSlug?: (value: string[] | ((prevVar: string[]) => string[])) => void;
}): JSX.Element => {
  const { t } = useTranslation('component');

  const onChange = (event: any) => {
    if (props.setNewsletterTypesSlug) {
      const slug = props.newsletterTypes.find(
        (newsletterType) => newsletterType.Id.toString() == event.target.value
      )?.Slug;
      if (slug) {
        props.setNewsletterTypesSlug((prevVar: string[]): string[] => {
          if (event.target.checked) {
            return [...prevVar, slug];
          } else {
            return [...prevVar.filter((val) => val != slug)];
          }
        });
      }
    }
    props.formik.handleChange(event);
  };

  return (
    <>
      <FormLabel sx={{ color: props.disableContentTypes ? '#38444D80' : '#38444D' }} id="NewsletterContentLabel">
        {t('CommunicationPreferences.NewsletterContentTitle')}
      </FormLabel>
      <RadioGroup aria-labelledby="NewsletterContentLabel">
        {props.newsletterTypes.map((newsletterType) => (
          <FormControlLabel
            disabled={props.disableContentTypes}
            key={newsletterType.NewsLetterName}
            id={newsletterType.NewsLetterName.replaceAll(' ', '')}
            name="newsletterTypes"
            value={newsletterType.Id}
            checked={props.formik.values.newsletterTypes.indexOf(newsletterType.Id.toString()) > -1}
            onChange={onChange}
            control={<Switch />}
            sx={{ alignItems: 'start', my: '6px' }}
            label={
              <>
                <Typography variant="subtitle1" sx={{ mt: '3px' }}>
                  {newsletterType.NewsLetterName}
                </Typography>
                <Typography variant="body2">
                  {/*todo: use a unique static "code" field instead of the name*/}

                  {t('CommunicationPreferences.' + newsletterType.NewsLetterName.replaceAll(' ', '') + 'LabelInfo')}
                </Typography>
              </>
            }
          />
        ))}
      </RadioGroup>
    </>
  );
};
